import type { RepairTruck } from '@otw/models/logistics'
import { getRepairTruckWorkStatus, getTruckStatus } from '@otw/models/logistics'
import { isActiveRepairTruckStatus } from '@otw/models/repair'
import type { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { getLastRepairGridUpdateMs } from '@/utils/repairLastUpdate'
import type { VehiclesV2Row } from '../components/views/VehiclesV2Grid'
import type { RepairV2GridRow } from '../components/views/RepairV2TrucksGrid'

const formatLabeledLines = (
  entries: Array<[string, string | number | boolean | null | undefined]>,
): string =>
  entries
    .map(([label, value]) => {
      const display =
        value === null || value === undefined || String(value).trim() === ''
          ? '—'
          : String(value)
      return `${label}: ${display}`
    })
    .join('\n')

export const getVehiclesV2TruckStatusLabel = (status?: string): string => {
  if (!status) return 'Draft'
  if (status === 'assigned') return 'Awaiting transport'
  if (status === 'awaiting_transport') return 'Awaiting transport'
  if (status === 'in_transit') return 'In transit'
  return status.replace(/_/g, ' ')
}

export const formatVehiclesV2RowForClipboard = (
  row: VehiclesV2Row,
  formatCurrency: (amount: number) => string,
): string =>
  formatLabeledLines([
    ['Vehicle', row.vehicle],
    ['Stock #', row.stock],
    ['Tier', row.tier],
    ['VIN', row.vin],
    [
      'Estimated repair cost',
      typeof row.estimatedRepairCost === 'number'
        ? formatCurrency(row.estimatedRepairCost)
        : null,
    ],
    [
      'Funded date',
      row.fundedDate
        ? new Date(row.fundedDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : null,
    ],
    [
      'Paid date',
      row.paidDate
        ? new Date(row.paidDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : null,
    ],
    ['Customer', row.customer],
    ['Transporters', row.transporters.join(', ')],
    ['Inventory status', row.inventoryStatus],
    ['Current location', row.location],
    ['Ready for pickup', row.readyForPickup ? 'Yes' : 'No'],
    ['Truck status', getVehiclesV2TruckStatusLabel(row.status)],
    ['Flagged', row.flagged ? 'Yes' : 'No'],
    ['Transport truck ID', row.id],
    ['Inventory vehicle ID', row.inventoryVehicleId],
    ['Repair truck ID', row.repairTruckId],
  ])

export function formatDaysAgoForClipboard(ms: number): string {
  const elapsedMs = Math.max(0, Date.now() - ms)
  const days = Math.floor(elapsedMs / (24 * 60 * 60 * 1000))
  return `${days} day${days === 1 ? '' : 's'} ago`
}

function formatDurationMsForClipboard(durationMs: number): string {
  const safeMs = Math.max(0, durationMs)
  const days = Math.floor(safeMs / (24 * 60 * 60 * 1000))
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`
  const hours = Math.floor(safeMs / (60 * 60 * 1000))
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`
  const minutes = Math.floor(safeMs / (60 * 1000))
  return minutes < 1 ? '< 1 min' : `${minutes} min`
}

function formatTimeInRepairForClipboard(
  row: TransportTruckWithVehicle,
  repairTruck: RepairTruck | null,
): string {
  if (!repairTruck) return '—'
  const stintStartMs = repairTruck.created_at?.toMillis?.()
  if (!stintStartMs) return '—'

  const isActiveStint = isActiveRepairTruckStatus(getTruckStatus(row.truck))

  if (isActiveStint) {
    return formatDurationMsForClipboard(Date.now() - stintStartMs)
  }

  const completeEntry = (repairTruck.work_status_history ?? []).find(
    (entry) => entry.status === 'complete',
  )
  const completedMs = completeEntry?.timestamp?.toMillis?.()
  const latestWorkStatusMs = repairTruck.work_status_history?.length
    ? repairTruck.work_status_history[repairTruck.work_status_history.length - 1]?.timestamp?.toMillis?.()
    : undefined
  const latestWorkNoteMs = repairTruck.work_notes?.length
    ? repairTruck.work_notes[repairTruck.work_notes.length - 1]?.timestamp?.toMillis?.()
    : undefined
  const stintEndMs = completedMs ?? latestWorkStatusMs ?? latestWorkNoteMs ?? stintStartMs

  return formatDurationMsForClipboard(stintEndMs - stintStartMs)
}

function formatCompletedOnDateForClipboard(repairTruck: RepairTruck): string {
  const completeEntry = [...(repairTruck.work_status_history ?? [])]
    .reverse()
    .find((entry) => entry.status === 'complete')
  const completedMs = completeEntry?.timestamp?.toMillis?.()
  if (!completedMs) return '—'
  return new Date(completedMs).toLocaleDateString('en-US')
}

export interface FormatRepairV2RowClipboardContext {
  getRepairShopLabel: (row: RepairV2GridRow) => string
  getCustomerLabel: (row: RepairV2GridRow) => string
  getInventoryStatusLabel: (row: RepairV2GridRow) => string
  getPriorityLabel: (row: RepairV2GridRow) => string
  getLastUpdateLabel: (row: RepairV2GridRow) => string
  getChecklistSummaryLabel?: (row: RepairV2GridRow) => string
}

export const formatRepairV2RowForClipboard = (
  row: RepairV2GridRow,
  ctx: FormatRepairV2RowClipboardContext,
): string => {
  const vehicle = row.truckWithVehicle.vehicle
  const vehicleLabel = vehicle
    ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim() || '—'
    : '—'
  const stock = vehicle?.stock_number ?? '—'
  const vin =
    row.truckWithVehicle.truck?.vin ?? row.truckWithVehicle.vehicle?.vin ?? '—'
  const repairTruck = row.repairTruck
  const workNotes = repairTruck?.work_notes
  const latestWorkNote = workNotes?.length ? workNotes[workNotes.length - 1].note : null

  const lines: Array<[string, string | number | boolean | null | undefined]> = [
    ['Priority', ctx.getPriorityLabel(row)],
    ['Last update', ctx.getLastUpdateLabel(row)],
    ['Vehicle', vehicleLabel],
    ['Stock #', stock],
    ['VIN', vin],
    ['Repair shop', ctx.getRepairShopLabel(row)],
    ['Customer', ctx.getCustomerLabel(row)],
    ['Inventory status', ctx.getInventoryStatusLabel(row)],
    [
      'Funded date',
      vehicle?.funded_date
        ? new Date(vehicle.funded_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : null,
    ],
    [
      'Paid date',
      (() => {
        const paidDate = vehicle?.paid_for_date ?? vehicle?.title_tracking?.paid_date
        return paidDate
          ? new Date(paidDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : null
      })(),
    ],
    [
      'Time in repair',
      formatTimeInRepairForClipboard(row.truckWithVehicle, repairTruck),
    ],
    ['Repairs completed', formatCompletedOnDateForClipboard(repairTruck)],
    ['Work status', getRepairTruckWorkStatus(repairTruck)],
    ['Work notes', latestWorkNote],
    ['Paid', repairTruck.paid ? 'Yes' : 'No'],
    ['Flagged', repairTruck.flagged ? 'Yes' : 'No'],
    ['Transport truck ID', row.truckWithVehicle.truck?.id],
    ['Repair truck ID', repairTruck.id],
  ]

  const checklist = ctx.getChecklistSummaryLabel?.(row)
  if (checklist) {
    lines.push(['Inspection checklist', checklist])
  }

  return formatLabeledLines(lines)
}

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
